/**
 * Custom hook for fetching and managing show and holds data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { Show, HoldList } from 'api-client/models';
import { useShowsApi, useAppContext } from '@/context/AppContext';
import { apiDateToDateOnly } from '@/shows/utils/dateOnly';

interface UseShowDataResult {
  show: Show | null;
  holds: HoldList[];
  loading: boolean;
  holdsLoading: boolean;
  error: string | null;
  infoMessage: string | null;
  setInfoMessage: (message: string | null) => void;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage show data and its associated holds
 * @param showId - The ID of the show to fetch
 */
export const useShowData = (showId: string | undefined): UseShowDataResult => {
  const location = useLocation();
  const showsApi = useShowsApi();
  const { apiClients } = useAppContext();
  const requestId = useRef(0);
  const mounted = useRef(true);
  const activeScope = useRef(showId);
  activeScope.current = showId;
  const [data, setData] = useState<{
    scope: string | undefined;
    show: Show | null;
    holds: HoldList[];
    loading: boolean;
    holdsLoading: boolean;
    showError: string | null;
    holdsError: string | null;
  }>({
    scope: showId, show: null, holds: [], loading: Boolean(showId),
    holdsLoading: Boolean(showId), showError: null, holdsError: null,
  });
  const [message, setMessage] = useState<{ scope: string | undefined; value: string | null }>({
    scope: showId, value: null,
  });

  const load = useCallback(async (): Promise<string | null> => {
    if (!mounted.current) return 'The show page is no longer mounted.';
    if (activeScope.current !== showId) return 'The show changed before refresh could start.';
    const currentRequest = ++requestId.current;
    const isCurrent = () => mounted.current && currentRequest === requestId.current && activeScope.current === showId;
    setData((previous) => ({
      scope: showId,
      show: previous.scope === showId ? previous.show : null,
      holds: previous.scope === showId ? previous.holds : [],
      loading: Boolean(showId), holdsLoading: Boolean(showId),
      showError: null, holdsError: null,
    }));
    if (!showId) return null;

    const fetchShow = async (): Promise<string | null> => {
      try {
        const show = await showsApi.showsRetrieve({ id: showId });
        if (isCurrent()) setData((previous) => ({ ...previous, show, loading: false }));
        return null;
      } catch {
        const error = 'Failed to load show. Please try again.';
        if (isCurrent()) setData((previous) => ({ ...previous, showError: error, loading: false }));
        return error;
      }
    };

    const fetchHolds = async (): Promise<string | null> => {
      try {
        const fetched: HoldList[] = [];
        let page = 1;
        while (isCurrent()) {
          const response = await apiClients.holds.holdsList({ page, show: showId });
          if (!isCurrent()) return null;
          fetched.push(...(response.results || []));
          if (!response.next) break;
          page += 1;
        }
        const holds = fetched.filter((hold) => hold.show === showId).sort(
          (a, b) => apiDateToDateOnly(a.holdDate).localeCompare(apiDateToDateOnly(b.holdDate)),
        );
        if (isCurrent()) setData((previous) => ({ ...previous, holds, holdsLoading: false }));
        return null;
      } catch {
        const error = 'Failed to load show dates. Please try again.';
        if (isCurrent()) setData((previous) => ({ ...previous, holdsError: error, holdsLoading: false }));
        return error;
      }
    };

    const errors = (await Promise.all([fetchShow(), fetchHolds()])).filter(Boolean);
    if (!isCurrent()) return 'Refresh was superseded by a newer show request.';
    return errors.length > 0 ? errors.join(' ') : null;
  }, [showId, showsApi, apiClients.holds]);

  const refetch = async () => {
    const error = await load();
    if (error) throw new Error(error);
  };

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => { mounted.current = false; requestId.current += 1; };
  }, [load]);

  useEffect(() => {
    const state: unknown = location.state;
    if (typeof state === 'object' && state !== null && 'message' in state
      && typeof state.message === 'string') {
      setMessage({ scope: showId, value: state.message });
      // Preserve React Router's history key/index and unrelated navigation state.
      const { message: _message, ...remainingState } = state;
      window.history.replaceState({ ...window.history.state, usr: remainingState }, document.title);
    } else {
      setMessage({ scope: showId, value: null });
    }
  }, [showId, location.key]);

  const current = data.scope === showId;

  return {
    show: current ? data.show : null,
    holds: current ? data.holds : [],
    loading: current ? data.loading : Boolean(showId),
    holdsLoading: current ? data.holdsLoading : Boolean(showId),
    error: current ? [data.showError, data.holdsError].filter(Boolean).join(' ') || null : null,
    infoMessage: message.scope === showId ? message.value : null,
    setInfoMessage: (value) => setMessage({ scope: showId, value }),
    refetch,
  };
};
